import { DOCUMENT, inject, Injectable, InjectionToken } from '@angular/core';
import { AnalyticsEvent, AnalyticsLegacyEvent } from '@core/enums/analytics.enum';
import {
  IAnalyticsEventProperties,
  IAnalyticsSuperProperties,
} from '@core/interfaces/analytics.interface';
import { MixpanelService } from '@core/services/mixpanel.service';
import { environment } from '@environments/environment';
import { GoogleAnalyticsService } from 'ngx-google-analytics';

type AnalyticsParams = Record<string, string | number | boolean | null | undefined>;

const GA4_EVENT_PROPERTIES: ReadonlySet<string> = new Set([
  'clinic_name',
  'clinic_billing_status',
  'clinic_country',
  'user_role',
  'app_type',
  'method',
  'module',
  'route_template',
]);

const ANALYTICS_EMPTY_VALUE = 'na';

const fillEmptyAnalyticsProperties = (values: object): AnalyticsParams =>
  Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      value === null || value === undefined || value === '' ? ANALYTICS_EMPTY_VALUE : value,
    ]),
  ) as AnalyticsParams;

const toOrigin = (url: string): string => {
  try {
    return new URL(url).origin;
  } catch {
    return '';
  }
};

export const ANALYTICS_ENABLED = new InjectionToken<boolean>('ANALYTICS_ENABLED', {
  providedIn: 'root',
  factory: () => environment.analyticsEnabled,
});

@Injectable({ providedIn: 'root' })
export class AnalyticsTrackingService {
  private readonly enabled = inject(ANALYTICS_ENABLED);
  private readonly mixpanelService = inject(MixpanelService);
  private readonly gaService = inject(GoogleAnalyticsService);
  private readonly document = inject(DOCUMENT);
  private readonly entryReferrer = toOrigin(this.document.referrer);
  private superProperties: IAnalyticsSuperProperties | null = null;
  private pageLocation: string | null = null;

  constructor() {
    if (!this.enabled) return;
    this.setGa4Page(this.document.location.origin, this.entryReferrer);
  }

  setPage(routeTemplate: string): void {
    if (!this.enabled) return;
    const pageLocation = `${this.document.location.origin}${routeTemplate}`;
    this.setGa4Page(pageLocation, this.pageLocation ?? this.entryReferrer);
    this.pageLocation = pageLocation;
  }

  setContext(superProperties: IAnalyticsSuperProperties): void {
    if (!this.enabled) return;
    this.superProperties = superProperties;
    this.mixpanelService.register(fillEmptyAnalyticsProperties(superProperties));
  }

  track<E extends AnalyticsEvent>(event: E, properties: IAnalyticsEventProperties[E]): void {
    if (!this.enabled) return;
    const eventProperties = fillEmptyAnalyticsProperties(properties);
    this.mixpanelService.track(event, eventProperties);
    this.gaService.gtag('event', event, this.toGa4Params(eventProperties));
  }

  trackLegacy(event: AnalyticsLegacyEvent, properties: AnalyticsParams): void {
    if (!this.enabled) return;
    this.gaService.gtag('event', event, this.toGa4Params(fillEmptyAnalyticsProperties(properties)));
  }

  reset(): void {
    if (!this.enabled) return;
    this.mixpanelService.reset();
    this.gaService.gtag('set', { user_id: null });
    if (this.superProperties) {
      this.mixpanelService.register(fillEmptyAnalyticsProperties(this.superProperties));
    }
  }

  private setGa4Page(pageLocation: string, pageReferrer: string): void {
    this.gaService.gtag('set', { page_location: pageLocation, page_referrer: pageReferrer });
  }

  private toGa4Params(eventProperties: object): AnalyticsParams {
    const params = {
      ...fillEmptyAnalyticsProperties(this.superProperties ?? {}),
      ...eventProperties,
    };
    return Object.fromEntries(
      Object.entries(params).filter(([key]) => GA4_EVENT_PROPERTIES.has(key)),
    ) as AnalyticsParams;
  }
}
